import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import CommonTable1 from "app/components/Tables/CommonTable1";
import CustomLoader from "app/components/common/CustomLoader";
import AddEditPlanDialog from "../../components/AddEditPlanDialog";
import {
  getAllPlans,
  deletePlan,
  getProduct,
} from "app/services/org/subscription.service";
import useSelectorHook from "app/services/redux/useSelectorHook";
import TableActionDropdown from "app/components/common/tableComponents/TableActionDropdown";
import CommonDialog from "app/components/common/CommonDialog";

const Plans = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { selectedSession } = useSelectorHook();

  const [state, setState] = useState({
    addEditDialog: false,
    viewDialog: false,
    deleteDialog: false,
    filterParams: {},
    page: 1,
    editData: null,
    deleteData: null,
    viewData: null,
  });

  const [productList, setProductList] = useState([]);

  // Fetch Products for dropdown
  const { isLoading: productsLoading } = useQuery(
    "getProductForPlanDropdown",
    () => getProduct({ session: selectedSession }),
    {
      onSuccess: (res) => {
        setProductList(res?.data?.data || []);
      },
      onError: (err) => {
        toast.error("Failed to load products");
      },
    },
  );

  // Fetch Plans
  const {
    isLoading: plansLoading,
    isFetching: plansFetching,
    data: plansData,
    refetch: refetchPlans,
  } = useQuery(
    ["getAllPlans", state.filterParams, state.page, selectedSession],
    () =>
      getAllPlans({
        ...state.filterParams,
        page: state.page,
        limit: 10,
        session: selectedSession,
      }),
    {
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to load plans");
      },
    },
  );

  // Delete Plan Mutation
  const { mutate: deletePlanMutate, isLoading: deletePlanLoading } =
    useMutation("deletePlan", deletePlan, {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan deleted successfully");
        refetchPlans();
        handleCloseDeleteDialog();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to delete plan");
      },
    });

  const handleOpenAddDialog = () => {
    setState((prev) => ({
      ...prev,
      addEditDialog: true,
      editData: null,
    }));
  };

  const handleOpenEditDialog = (data) => {
    setState((prev) => ({
      ...prev,
      addEditDialog: true,
      editData: data,
    }));
  };

  const handleOpenViewDialog = (data) => {
    setState((prev) => ({
      ...prev,
      viewDialog: true,
      viewData: data,
    }));
  };

  const handleOpenDeleteDialog = (data) => {
    setState((prev) => ({
      ...prev,
      deleteDialog: true,
      deleteData: data,
    }));
  };

  const handleCloseAddDialog = () => {
    setState((prev) => ({
      ...prev,
      addEditDialog: false,
      editData: null,
    }));
  };

  const handleCloseViewDialog = () => {
    setState((prev) => ({
      ...prev,
      viewDialog: false,
      viewData: null,
    }));
  };

  const handleCloseDeleteDialog = () => {
    setState((prev) => ({
      ...prev,
      deleteDialog: false,
      deleteData: null,
    }));
  };

  const handleConfirmDelete = () => {
    if (state.deleteData?.id) {
      deletePlanMutate(state.deleteData.id);
    }
  };

  const handlePageChange = (newPage) => {
    setState((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "planName",
        header: "Plan Name",
        size: 150,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.original.planName}
          </Typography>
        ),
      },
      {
        accessorKey: "productId",
        header: "Product",
        size: 120,
        Cell: ({ row }) => {
          const product = productList.find(
            (p) => p.id === row.original.productId,
          );
          return (
            <Typography variant="body2">
              {product?.productName || "N/A"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "billingCycle",
        header: "Billing Cycle",
        size: 120,
        Cell: ({ row }) => (
          <Chip
            label={
              row.original.billingCycle?.charAt(0).toUpperCase() +
              row.original.billingCycle?.slice(1)
            }
            size="small"
          />
        ),
      },
      {
        accessorKey: "price",
        header: "Price",
        size: 100,
        Cell: ({ row }) => (
          <Typography variant="body2">
            ₹{parseFloat(row.original.price).toFixed(2) || 0}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.status}
            size="small"
            color={row.original.status === "active" ? "success" : "default"}
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: "isPublic",
        header: "Visibility",
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.isPublic ? "Public" : "Private"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: "maxUsers",
        header: "Max Users",
        size: 100,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.maxUsers || "Unlimited"}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 100,
        Cell: ({ row }) => (
          <TableActionDropdown>
            <Box
              onClick={() => handleOpenViewDialog(row.original)}
              sx={{
                p: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": { backgroundColor: "#f6f8fc" },
              }}
            >
              <Visibility fontSize="small" />
              View
            </Box>
            <Box
              onClick={() => handleOpenEditDialog(row.original)}
              sx={{
                p: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": { backgroundColor: "#f6f8fc" },
              }}
            >
              <Edit fontSize="small" />
              Edit
            </Box>
            <Box
              onClick={() => handleOpenDeleteDialog(row.original)}
              sx={{
                p: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "error.main",
                "&:hover": { backgroundColor: "#f6f8fc" },
              }}
            >
              <Delete fontSize="small" />
              Delete
            </Box>
          </TableActionDropdown>
        ),
      },
    ],
    [productList],
  );

  const tableData = useMemo(() => plansData?.data?.data || [], [plansData]);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Helmet>
        <title>Plans Management | Ekalsutra</title>
      </Helmet>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Subscription Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAddDialog}
        >
          Add Plan
        </Button>
      </Box>

      {/* Table */}
      {plansLoading ? (
        <CustomLoader />
      ) : (
        <CommonTable1
          columns={columns}
          data={tableData}
          enablePagination={true}
          pageCount={Math.ceil((plansData?.data?.total || 0) / 10)}
          state={{
            pagination: {
              pageIndex: state.page - 1,
              pageSize: 10,
            },
          }}
          onPaginationChange={(updater) => {
            const newPaginationState = updater();
            handlePageChange(newPaginationState.pageIndex);
          }}
        />
      )}

      {/* Dialogs */}
      <AddEditPlanDialog
        open={state.addEditDialog}
        onClose={handleCloseAddDialog}
        refetch={refetchPlans}
        edit={!!state.editData}
        editData={state.editData}
        productList={productList}
      />

      {/* View Plan Dialog */}
      <CommonDialog
        open={state.viewDialog}
        onClose={handleCloseViewDialog}
        title="Plan Details"
        maxWidth="sm"
      >
        {state.viewData && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: "gray" }}>
                  Plan Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {state.viewData.planName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: "gray" }}>
                  Description
                </Typography>
                <Typography variant="body2">
                  {state.viewData.description}
                </Typography>
              </Box>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Price
                  </Typography>
                  <Typography variant="body1">
                    ₹{parseFloat(state.viewData.price).toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Billing Cycle
                  </Typography>
                  <Typography variant="body1">
                    {state.viewData.billingCycle}
                  </Typography>
                </Box>
              </Box>
              {state.viewData.features &&
                state.viewData.features.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "gray", mb: 1 }}
                    >
                      Features
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {state.viewData.features.map((feature, idx) => (
                        <Chip key={idx} label={feature} size="small" />
                      ))}
                    </Stack>
                  </Box>
                )}
            </Stack>
          </Box>
        )}
      </CommonDialog>

      {/* Delete Confirmation Dialog */}
      <CommonDialog
        open={state.deleteDialog}
        onClose={handleCloseDeleteDialog}
        title="Confirm Delete"
        maxWidth="xs"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the plan "
            {state.deleteData?.planName}"?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleCloseDeleteDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              loading={deletePlanLoading}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </CommonDialog>
    </Container>
  );
};

export default Plans;
